import { useEffect, useState, useCallback } from "react";
import { useApp } from "../lib/store";
import { api, fmtEur, fmtDate, errDetail } from "../lib/api";
import { CONTRACT_STATUS_COLORS } from "../lib/fields";
import { ListPage } from "../components/ListPage";
import { EntityForm } from "../components/EntityForm";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ContractDocumentsDialog, ContractDocumentsPanel } from "../components/ContractDocuments";
import { AlertTriangle, TrendingUp, Save, RefreshCw, Upload, LineChart as LineChartIcon, FileText } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const INDEX_COLORS = ["#1B2A4A", "#F5A623", "#10b981", "#8b5cf6", "#ef4444", "#0ea5e9"];

export default function Contracts() {
  const { t, isManager, canWrite } = useApp();
  const [indexes, setIndexes] = useState({});
  const [edited, setEdited] = useState(null);
  const [history, setHistory] = useState([]);
  const [indexNames, setIndexNames] = useState([]);
  const [months, setMonths] = useState(24);
  const [importOpen, setImportOpen] = useState(false);
  const [renewing, setRenewing] = useState(null);
  const [docsFor, setDocsFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [listKey, setListKey] = useState(0);

  const loadIdx = useCallback(() => api.get("/price-indexes").then((r) => setIndexes(r.data.values || {})).catch(() => {}), []);
  const loadHistory = useCallback(() => {
    api.get(`/price-indexes/history?months=${months}`).then((r) => {
      setHistory(r.data.history || []);
      setIndexNames(r.data.index_names || []);
    }).catch(() => {});
  }, [months]);

  useEffect(() => { loadIdx(); }, [loadIdx]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const saveIdx = async () => {
    try {
      const r = await api.put("/price-indexes", { values: edited });
      setIndexes(r.data.values);
      setEdited(null);
      loadHistory();
      toast.success(`${t("save")} ✓ – ${r.data.alerts_triggered} ${t("index_alerts")}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const deviation = (r) => {
    const cur = indexes[r.price_index];
    const base = Number(r.index_base_value);
    if (!cur || !base) return null;
    return ((cur - base) / base) * 100;
  };

  // Build chart data: array of { date, [index_name]: value, ... }
  const chartData = (() => {
    const byDate = {};
    history.forEach((h) => {
      byDate[h.date] = byDate[h.date] || { date: h.date };
      byDate[h.date][h.index_name] = h.value;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  })();

  const columns = [
    { key: "ai_docs", label: t("ai_contract_analysis"), render: (r) => (
      canWrite ? (
        <Button size="sm" data-testid={`ai-docs-btn-${r.contract_number}`}
          className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold whitespace-nowrap"
          onClick={(e) => { e.stopPropagation(); setDocsFor(r); }}>
          <Upload className="h-3.5 w-3.5 mr-1" />{t("ai_contract_analysis")}
        </Button>
      ) : <span className="text-gray-300">–</span>
    ) },
    { key: "contract_number", label: t("contract_number"), render: (r) => (
      <span className="font-mono text-xs" data-testid={`contract-number-${r.contract_number}`}>{r.contract_number}</span>) },
    { key: "name", label: t("name"), render: (r) => <span className="font-medium text-[#1B2A4A]">{r.name}</span> },
    { key: "account_name", label: t("account") },
    { key: "end_date", label: t("end_date"), render: (r) => fmtDate(r.end_date) },
    { key: "days_to_expiry", label: t("days_to_expiry"), align: "right", render: (r) => (
      r.days_to_expiry == null ? "–" : (
        <span className={`font-mono flex items-center justify-end gap-1 ${r.days_to_expiry < 0 ? "text-red-600" : r.days_to_expiry <= 90 ? "text-amber-600 font-semibold" : "text-gray-600"}`}>
          {r.days_to_expiry <= 90 && r.days_to_expiry >= 0 && <AlertTriangle className="h-3.5 w-3.5" />}
          {r.days_to_expiry}
        </span>
      )) },
    { key: "annual_value", label: t("annual_value"), align: "right", render: (r) => fmtEur(r.annual_value) },
    { key: "price_index", label: t("price_index") },
    { key: "index_dev", label: t("index_deviation"), align: "right", render: (r) => {
      const dev = deviation(r);
      if (dev == null) return <span className="text-gray-300">–</span>;
      const breach = Math.abs(dev) >= Number(r.indexation_threshold_pct || 0) && Number(r.indexation_threshold_pct);
      return (
        <span data-testid="index-deviation" className={`font-mono flex items-center justify-end gap-1 ${breach ? "text-red-600 font-bold" : "text-gray-600"}`}>
          {breach && <AlertTriangle className="h-3.5 w-3.5" />}
          {dev > 0 ? "+" : ""}{dev.toFixed(1)}%
        </span>
      );
    } },
    { key: "indexation_threshold_pct", label: t("indexation_threshold"), align: "right", render: (r) => r.indexation_threshold_pct ? `±${r.indexation_threshold_pct}%` : "–" },
    { key: "computed_status", label: t("status"), render: (r) => (
      <Badge className={`${CONTRACT_STATUS_COLORS[r.computed_status] || ""} hover:bg-inherit`} data-testid="contract-status-badge">
        {r.computed_status === "Expiring Soon" ? t("expiring_soon") : r.computed_status === "Expired" ? t("expired") : r.computed_status}
      </Badge>) },
    { key: "owner_name", label: t("owner") },
    { key: "renew_action", label: "", align: "right", render: (r) => (
      r.computed_status === "Renewed" || r.status === "Terminated" ? (
        r.renewed_to_number ? <span className="text-xs text-gray-400 font-mono">→ {r.renewed_to_number}</span> : null
      ) : canWrite ? (
        <Button size="sm" variant="outline" data-testid={`renew-btn-${r.contract_number}`}
          onClick={(e) => { e.stopPropagation(); setRenewing(r); }}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" />{t("renew")}
        </Button>
      ) : null
    ) },
  ];

  return (
    <div className="space-y-4">
      {/* Current values + edit */}
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="price-index-panel">
        <div className="flex flex-wrap items-center gap-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />{t("current_indexes")}
          </p>
          {Object.entries(indexes).map(([name, val]) => (
            <div key={name} className="flex items-center gap-2" data-testid={`index-${name}`}>
              <span className="text-sm text-gray-600">{name}:</span>
              {edited ? (
                <Input className="h-8 w-24 font-mono text-sm" type="number" step="0.1" value={edited[name] ?? ""}
                  data-testid={`index-input-${name}`}
                  onChange={(e) => setEdited((ed) => ({ ...ed, [name]: e.target.value }))} />
              ) : (
                <span className="font-mono font-bold text-[#1B2A4A]">{Number(val).toFixed(1)}</span>
              )}
            </div>
          ))}
          {isManager && (
            <div className="ml-auto flex items-center gap-2">
              {edited ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEdited(null)} data-testid="cancel-indexes-btn">{t("cancel")}</Button>
                  <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={saveIdx} data-testid="save-indexes-btn">
                    <Save className="h-3.5 w-3.5 mr-1" />{t("save")}
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import-btn">
                    <Upload className="h-3.5 w-3.5 mr-1" />{t("import_indexes")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEdited({ ...indexes })} data-testid="edit-indexes-btn">
                    {t("edit")}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Index history chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="index-history-card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
            <LineChartIcon className="h-3.5 w-3.5" />{t("index_trend")}
          </p>
          <div className="flex gap-1" data-testid="history-range-tabs">
            {[12, 24, 36].map((m) => (
              <Button key={m} size="sm" variant={months === m ? "default" : "outline"}
                className={months === m ? "bg-[#1B2A4A] hover:bg-[#2a3d63]" : ""}
                onClick={() => setMonths(m)} data-testid={`range-${m}m-btn`}>
                {t(`last_${m}m`)}
              </Button>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center" data-testid="no-history-msg">{t("no_history_yet")}</p>
        ) : (
          <div style={{ width: "100%", height: 260 }} data-testid="index-history-chart">
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {indexNames.map((n, i) => (
                  <Line key={n} type="monotone" dataKey={n} stroke={INDEX_COLORS[i % INDEX_COLORS.length]}
                        strokeWidth={2} dot={{ r: 2 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <ListPage key={listKey} module="contracts" titleKey="contracts" columns={columns}
        onRowClick={(r) => setEditing(r)} />

      {editing && (
        <ContractEditDialog contract={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setListKey((k) => k + 1); }} />
      )}

      {renewing && (
        <RenewDialog contract={renewing} onClose={() => setRenewing(null)} onDone={() => {
          setRenewing(null);
          setListKey((k) => k + 1);
          toast.success(t("contract_renewed"));
        }} />
      )}

      {docsFor && (
        <ContractDocumentsDialog contract={docsFor} onClose={() => setDocsFor(null)} />
      )}

      {importOpen && (
        <ImportDialog indexNames={Object.keys(indexes)} onClose={() => setImportOpen(false)}
          onDone={(r) => {
            setImportOpen(false);
            toast.success(`${t("import_result")}: ${r.imported}, ${r.skipped} ${t("rows_skipped")} – ${r.alerts_triggered} ${t("index_alerts")}`);
            loadIdx();
            loadHistory();
          }} />
      )}
    </div>
  );
}


export function ContractEditDialog({ contract, onClose, onSaved }) {
  const { t } = useApp();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-edit-dialog">
        <DialogHeader>
          <DialogTitle>{t("edit")} – {contract.contract_number || t("contracts")}</DialogTitle>
          <DialogDescription>{contract.name} · {contract.account_name}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="data">
          <TabsList>
            <TabsTrigger value="data" data-testid="contract-tab-data">{t("contract_data")}</TabsTrigger>
            <TabsTrigger value="docs" data-testid="contract-tab-docs">
              <FileText className="h-3.5 w-3.5 mr-1.5" />{t("documents")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="data" className="pt-2">
            <EntityForm module="contracts" initial={contract} onSaved={onSaved} onCancel={onClose} />
          </TabsContent>
          <TabsContent value="docs" className="pt-2">
            <ContractDocumentsPanel contract={contract} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}


function RenewDialog({ contract, onClose, onDone }) {
  const { t } = useApp();
  const [months, setMonths] = useState(12);
  const [name, setName] = useState(`${contract.name} (Renewal)`);
  const [annualValue, setAnnualValue] = useState(contract.annual_value || "");
  const [startDate, setStartDate] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { duration_months: Number(months), name, annual_value: Number(annualValue) || null };
      if (startDate) body.start_date = startDate;
      await api.post(`/contracts/${contract.id}/renew`, body);
      onDone();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="renew-dialog">
        <DialogHeader><DialogTitle>{t("renew_contract")} – {contract.contract_number}</DialogTitle>
          <DialogDescription>{contract.account_name} · {t("end_date")}: {contract.end_date}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="renew-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("new_start_date")}</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="renew-start-input" />
              <p className="text-[10px] text-gray-400 mt-1">Auto: {t("end_date")} + 1</p>
            </div>
            <div>
              <Label>{t("duration_months")}</Label>
              <Input type="number" min="1" value={months} onChange={(e) => setMonths(e.target.value)} data-testid="renew-duration-input" />
            </div>
          </div>
          <div>
            <Label>{t("annual_value")} (€)</Label>
            <Input type="number" value={annualValue} onChange={(e) => setAnnualValue(e.target.value)} data-testid="renew-value-input" />
          </div>
          <div className="text-xs text-gray-500 bg-slate-50 rounded p-2 border border-slate-200">
            {t("price_index")}: <b>{contract.price_index}</b> – {t("index_base_value")} wird auf aktuellen Wert gesetzt.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button disabled={saving} onClick={submit} className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" data-testid="renew-submit-btn">
            <RefreshCw className="h-4 w-4 mr-1" />{t("renew")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ImportDialog({ indexNames, onClose, onDone }) {
  const { t } = useApp();
  const [tab, setTab] = useState("paste");
  const [csv, setCsv] = useState("");
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState("");
  const [saving, setSaving] = useState(false);

  const previewRows = (() => {
    const text = csv.trim();
    if (!text) return { headers: [], rows: [] };
    const delim = text.includes("\t") ? "\t" : text.includes(";") ? ";" : ",";
    const lines = text.split(/\r?\n/).slice(0, 6);
    const headers = lines[0].split(delim).map((s) => s.trim());
    const rows = lines.slice(1).map((l) => l.split(delim));
    return { headers, rows };
  })();

  const readFile = async (f) => {
    if (!f) return;
    setFile(f);
    const text = await f.text();
    setCsv(text);
  };

  const submit = async () => {
    if (!csv.trim()) { toast.error("CSV leer"); return; }
    setSaving(true);
    try {
      const body = { csv };
      if (target) body.index_name = target;
      const r = await api.post("/price-indexes/import", body);
      onDone(r.data);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="import-dialog">
        <DialogHeader><DialogTitle><Upload className="inline h-4 w-4 mr-2" />{t("import_indexes")}</DialogTitle>
          <DialogDescription>{t("import_index_help")}</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="paste" data-testid="import-tab-paste">{t("paste_csv")}</TabsTrigger>
            <TabsTrigger value="upload" data-testid="import-tab-upload">{t("upload_file")}</TabsTrigger>
          </TabsList>
          <TabsContent value="paste">
            <Textarea rows={8} placeholder={`date;EUWID Papierpreisindex;Plastixx PP Index\n01.01.2026;110,5;125,3\n01.02.2026;111,8;126,9`}
              value={csv} onChange={(e) => setCsv(e.target.value)}
              className="font-mono text-xs" data-testid="import-csv-textarea" />
          </TabsContent>
          <TabsContent value="upload">
            <Input type="file" accept=".csv,.txt,.tsv" onChange={(e) => readFile(e.target.files?.[0])} data-testid="import-file-input" />
            {file && <p className="text-xs text-gray-500 mt-2">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
          </TabsContent>
        </Tabs>

        <div>
          <Label>{t("target_index")}</Label>
          <Select value={target || "__auto"} onValueChange={(v) => setTarget(v === "__auto" ? "" : v)}>
            <SelectTrigger data-testid="import-target-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto">{t("any_column")}</SelectItem>
              {indexNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {previewRows.headers.length > 0 && (
          <div className="border rounded p-2 max-h-40 overflow-auto" data-testid="import-preview">
            <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{t("preview")}</p>
            <table className="text-xs w-full">
              <thead><tr>{previewRows.headers.map((h) => <th key={h} className="text-left pr-3 font-medium text-gray-600">{h}</th>)}</tr></thead>
              <tbody>{previewRows.rows.map((r, i) => (
                <tr key={i} className="border-t border-gray-100">{r.map((c, j) => <td key={j} className="pr-3 py-1 font-mono">{c}</td>)}</tr>
              ))}</tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button disabled={saving || !csv.trim()} onClick={submit}
            className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" data-testid="import-submit-btn">
            <Upload className="h-4 w-4 mr-1" />{t("import")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
